import * as generalHelper from "../../../helpers/generalHelper";
import * as menuConstants from "../../../constants/menuConstants";
import * as methods from "../../../constants/serverMethods";
import * as navigationActions from "../../../actions/navigationActions";
import * as optionsConstants from "../../../constants/optionsConstants";
import * as priceConstants from "../../../constants/entityConstants/priceConstants";
import * as propertyConstants from "../../../constants/propertyConstants";
import * as tableHelper from "../../../helpers/tableHelper";

import { AppState } from "../../../reducers";
import { Currency } from "../../../types/currency";
import { Dispatch } from "redux";
import { Margin } from "../../../types/margin";
import { MarginType } from "../../../types/marginType";
import { MenuItem } from "../../../types/menu";
import { Package } from "../../../types/package";
import { Price } from "../../../types/price";
import { PriceOption } from "../../../types/priceOption";
import { ServerRequest } from "../../../types/serverRequest";
import { SystemPriceScene } from "../../../components/scenes/system/SystemPriceScene";

import { connect } from "react-redux";

export type PropsType = Readonly<{
    menuScene: MenuItem | null;
    activeBookmark: string | null;
    currency: Currency | null;
    priceLoading: boolean;
    // price generic
    priceGenericTableConstant: string;
    priceGenericList: Array<Price>;
    priceGenericActiveList: Array<Price>;
    priceGenericLoading: boolean;
    priceGenericButtonLoading: boolean;
    priceGenericColumnOrder: Array<propertyConstants.Property>;
    priceGenericColumnVisibility: Record<propertyConstants.Property, boolean>;
    priceGenericColumnWidth: Record<propertyConstants.Property, number>;
    // price groups
    priceGroupTableConstant: string;
    priceGroupList: Array<Price>;
    priceGroupActiveList: Array<Price>;
    priceGroupLoading: boolean;
    priceGroupButtonLoading: boolean;
    priceGroupColumnOrder: Array<propertyConstants.Property>;
    priceGroupColumnVisibility: Record<propertyConstants.Property, boolean>;
    priceGroupColumnWidth: Record<propertyConstants.Property, number>;
    // price margins
    marginTypeList: Array<MarginType>;
    marginTableConstant: string;
    marginList: Array<Margin>;
    marginActiveList: Array<Margin>;
    marginLoading: boolean;
    marginButtonLoading: boolean;
    marginColumnOrder: Array<propertyConstants.Property>;
    marginColumnVisibility: Record<propertyConstants.Property, boolean>;
    marginColumnWidth: Record<propertyConstants.Property, number>;
    marginSearch: string | null;
    marginSearchParams: any;
    marginShowGlobalSearch: boolean;
    marginSortingAsc: boolean;
    marginSortingCriterion: propertyConstants.Property | null;
    // price options
    priceOptionTableConstant: string;
    priceOptionList: Array<PriceOption>;
    priceOptionLoading: boolean;
    optionPriceDefinition: PriceOption | null;
    optionPriceEnableCompanyCost: PriceOption | null;
    // price surcharge
    packageTableConstant: string;
    packageList: Array<Package>;
    packageActiveList: Array<Package>;
    packageLoading: boolean;
    packageButtonLoading: boolean;
    packageColumnOrder: Array<propertyConstants.Property>;
    packageColumnVisibility: Record<propertyConstants.Property, boolean>;
    packageColumnWidth: Record<propertyConstants.Property, number>;
    packageOffset: number;
    packagePage: number;
    packageRowCount: number;
    packageRowCountCustom: boolean;
    packageSearch: string | null;
    packageSearchParams: Record<propertyConstants.Property, string>;
    packageShowGlobalSearch: boolean;
    packageSortingAsc: boolean;
    packageSortingCriterion: propertyConstants.Property | null;
    packageTotalCount: number | null;
}>;

export type DispatchType = Readonly<{
    setActiveBookmark(key: string | null): void;
}>;

const mapStateToProps = (state: AppState): PropsType => ({
    menuScene: generalHelper.getMenuItemByKey(
        state.navigation.menuList,
        menuConstants.MENU_MAIN_PAGE,
        menuConstants.PAGE_SYSTEM_PRICES
    ),
    activeBookmark: state.navigation.activeBookmark,
    currency: state.currency.systemCurrency,
    priceLoading: state.server.requests.some(
        (request: ServerRequest) =>
            request.method === methods.METHOD_GET_ALL_SYSTEM_PRICE_MARGINS ||
            request.method === methods.METHOD_GET_SYSTEM_PRICE_OPTIONS ||
            request.method === methods.METHOD_GET_SYSTEM_GENERIC_PRICE_DATA ||
            request.method === methods.METHOD_GET_SYSTEM_PRICE_GROUP_DATA ||
            request.method === methods.METHOD_GET_ALL_SYSTEM_PACKAGES
    ),
    // price generic
    priceGenericTableConstant: menuConstants.TABLE_SYSTEM_PRICE_GENERIC,
    priceGenericList: state.price.systemGenericList,
    priceGenericActiveList: state.price.systemGenericActiveList,
    priceGenericLoading: state.server.requests.some(
        (request: ServerRequest) =>
            request.method === methods.METHOD_GET_SYSTEM_PRICE_OPTIONS ||
            request.method === methods.METHOD_GET_SYSTEM_GENERIC_PRICE_DATA
    ),
    priceGenericButtonLoading: state.server.requests.some(
        (request: ServerRequest) => request.method === methods.METHOD_SET_SINGLE_SYSTEM_GENERIC_PRICE_DATA
    ),
    priceGenericColumnOrder: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_ORDER,
        menuConstants.TABLE_SYSTEM_PRICE_GENERIC
    ),
    priceGenericColumnVisibility: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_VISIBILITY,
        menuConstants.TABLE_SYSTEM_PRICE_GENERIC
    ),
    priceGenericColumnWidth: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_WIDTH,
        menuConstants.TABLE_SYSTEM_PRICE_GENERIC
    ),
    // price groups
    priceGroupTableConstant: menuConstants.TABLE_SYSTEM_PRICE_GROUPS,
    priceGroupList: state.price.systemPriceGroupList,
    priceGroupActiveList: state.price.systemPriceGroupActiveList,
    priceGroupLoading: state.server.requests.some(
        (request: ServerRequest) =>
            request.method === methods.METHOD_GET_SYSTEM_PRICE_OPTIONS ||
            request.method === methods.METHOD_GET_SYSTEM_PRICE_GROUP_DATA
    ),
    priceGroupButtonLoading: state.server.requests.some(
        (request: ServerRequest) => request.method === methods.METHOD_SET_SINGLE_SYSTEM_PRICE_GROUP_DATA
    ),
    priceGroupColumnOrder: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_ORDER,
        menuConstants.TABLE_SYSTEM_PRICE_GROUPS
    ),
    priceGroupColumnVisibility: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_VISIBILITY,
        menuConstants.TABLE_SYSTEM_PRICE_GROUPS
    ),
    priceGroupColumnWidth: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_WIDTH,
        menuConstants.TABLE_SYSTEM_PRICE_GROUPS
    ),
    // price margins
    marginTypeList: state.price.systemMarginTypeList,
    marginTableConstant: menuConstants.TABLE_SYSTEM_PRICE_MARGIN,
    marginList: state.price.systemMarginList,
    marginActiveList: state.price.systemMarginActiveList,
    marginLoading: state.server.requests.some(
        (request: ServerRequest) =>
            request.method === methods.METHOD_GET_SYSTEM_PRICE_OPTIONS ||
            request.method === methods.METHOD_GET_ALL_SYSTEM_PRICE_MARGINS ||
            request.method === methods.METHOD_DELETE_SYSTEM_PRICE_MARGIN
    ),
    marginButtonLoading: state.server.requests.some(
        (request: ServerRequest) => request.method === methods.METHOD_EDIT_SYSTEM_PRICE_MARGIN
    ),
    marginColumnOrder: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_ORDER,
        menuConstants.TABLE_SYSTEM_PRICE_MARGIN
    ),
    marginColumnVisibility: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_VISIBILITY,
        menuConstants.TABLE_SYSTEM_PRICE_MARGIN
    ),
    marginColumnWidth: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_WIDTH,
        menuConstants.TABLE_SYSTEM_PRICE_MARGIN
    ),
    marginSearch: state.price.systemMarginSearch,
    marginSearchParams: state.price.systemMarginSearchParams,
    marginShowGlobalSearch: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_SHOW_GLOBAL_SEARCH,
        menuConstants.TABLE_SYSTEM_PRICE_MARGIN
    ),
    marginSortingAsc: tableHelper.getSortingObjectValue(
        generalHelper.getObjectFromDictionaryByKey(
            state.login.options,
            optionsConstants.OPTION_TABLE_SORTING,
            menuConstants.TABLE_SYSTEM_PRICE_MARGIN
        )
    ),
    marginSortingCriterion: tableHelper.getSortingObjectKey(
        generalHelper.getObjectFromDictionaryByKey(
            state.login.options,
            optionsConstants.OPTION_TABLE_SORTING,
            menuConstants.TABLE_SYSTEM_PRICE_MARGIN
        )
    ),
    // price options
    priceOptionTableConstant: menuConstants.TABLE_SYSTEM_PRICE_OPTIONS,
    priceOptionList: state.price.systemOptionList,
    priceOptionLoading: state.server.requests.some(
        (request: ServerRequest) => request.method === methods.METHOD_GET_SYSTEM_PRICE_OPTIONS
    ),
    optionPriceDefinition: generalHelper.getObjectByKey(
        state.price.systemOptionList,
        priceConstants.PRICE_OPTION_PRICE_DEFINITION
    ),
    optionPriceEnableCompanyCost: generalHelper.getObjectByKey(
        state.price.systemOptionList,
        priceConstants.PRICE_OPTION_ENABLE_COMPANY_COST
    ),
    // price surcharge
    packageTableConstant: menuConstants.TABLE_SYSTEM_PRICE_SURCHARGE,
    packageList: state.package.systemList,
    packageActiveList: state.package.systemActiveList,
    packageLoading: state.server.requests.some(
        (request: ServerRequest) => request.method === methods.METHOD_GET_ALL_SYSTEM_PACKAGES
    ),
    packageButtonLoading: state.server.requests.some(
        (request: ServerRequest) => request.method === methods.METHOD_EDIT_SYSTEM_PACKAGE
    ),
    packageColumnOrder: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_ORDER,
        menuConstants.TABLE_SYSTEM_PRICE_SURCHARGE
    ),
    packageColumnVisibility: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_VISIBILITY,
        menuConstants.TABLE_SYSTEM_PRICE_SURCHARGE
    ),
    packageColumnWidth: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_COLUMNS_WIDTH,
        menuConstants.TABLE_SYSTEM_PRICE_SURCHARGE
    ),
    packageOffset: state.package.systemOffset,
    packagePage: state.package.systemPage,
    packageRowCount: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_ROW_COUNT,
        menuConstants.TABLE_SYSTEM_PRICE_SURCHARGE
    ),
    packageRowCountCustom: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_TABLE_ROW_COUNT_CUSTOM,
        menuConstants.TABLE_SYSTEM_PRICE_SURCHARGE
    ),
    packageSearch: state.package.systemSearch,
    packageSearchParams: state.package.systemSearchParams,
    packageShowGlobalSearch: generalHelper.getObjectFromDictionaryByKey(
        state.login.options,
        optionsConstants.OPTION_SHOW_GLOBAL_SEARCH,
        menuConstants.TABLE_SYSTEM_PRICE_SURCHARGE
    ),
    packageSortingAsc: tableHelper.getSortingObjectValue(
        generalHelper.getObjectFromDictionaryByKey(
            state.login.options,
            optionsConstants.OPTION_TABLE_SORTING,
            menuConstants.TABLE_SYSTEM_PRICE_SURCHARGE
        )
    ),
    packageSortingCriterion: tableHelper.getSortingObjectKey(
        generalHelper.getObjectFromDictionaryByKey(
            state.login.options,
            optionsConstants.OPTION_TABLE_SORTING,
            menuConstants.TABLE_SYSTEM_PRICE_SURCHARGE
        )
    ),
    packageTotalCount: state.package.systemCountTotal
});

// TODO setActiveBookmark move to Bookmark tab directly?
const mapDispatchToProps = (dispatch: Dispatch): DispatchType => ({
    setActiveBookmark: (key: string | null): any => dispatch(navigationActions.setActiveBookmark(key))
});
export const SystemPriceSceneContainer = connect(mapStateToProps, mapDispatchToProps)(SystemPriceScene);
